import { useCallback, useEffect, useRef, useState } from 'preact/hooks';
import { route } from 'preact-router';
import { useDispatch } from 'react-redux';
import { buildURL, get } from 'Shared/fetch';

import { useDefaultLandingPath } from '@/components/LayoutDetector/hooks';
import type { ProductSelection } from '@/components/products/CheckoutHandlerContext';
import { AuthPopupProvider } from '@/components/SocialLoginBar/AuthPopupContext';
import { postUserProductMutationFn } from '@/queries/products';
import { useSelector, useStore } from '@/redux/helper';
import { setUserData } from '@/redux/slices/user/actions';
import { withPopupMarker } from '@/routes/checkout/popup';
import { savePostAuthDestination } from '@/shared/postAuthDestination';

import { stepLabels } from './copy';
import { satisfiesGate } from './core/entitlement';
import { withResumeState } from './core/resumeState';
import useCheckoutPopup from './hooks/useCheckoutPopup';
import usePersistPersona from './hooks/usePersistPersona';
import useReportFacts from './hooks/useReportFacts';
import useResumeState from './hooks/useResumeState';
import useSpeedyFlowAnalytics from './hooks/useSpeedyFlowAnalytics';
import useSsoPopup from './hooks/useSsoPopup';
import { takePendingAction } from './pendingAction';
import speedyFlowSlice from './redux/slice';
import selectSpeedyFlowUser from './selectUser';
import SpeedyFlowShell from './SpeedyFlowShell';
import AwaitPaymentStep from './steps/AwaitPaymentStep';
import ChooseProductStep from './steps/ChooseProductStep';
import CompletionStep from './steps/CompletionStep';
import LogInStep from './steps/LogInStep';
import PersonaStep from './steps/PersonaStep';
import SignUpStep from './steps/SignUpStep';
import VerifyEmailStep from './steps/VerifyEmailStep';
import { GateRequirement } from './types';

const { advanceSpeedyFlow, closeSpeedyFlow } = speedyFlowSlice.actions;

/**
 * The Speedy Flow modal: renders whichever step the machine is on, inside the responsive shell.
 *
 * It owns the checkout popup because the popup outcome is a machine event, not a step concern —
 * the choose-product step only hands over a URL, and what happens after belongs to the flow.
 */
const SpeedyFlow = () => {
  const dispatch = useDispatch();
  const store = useStore();
  const { visible, machine, gate, ppt } = useSelector((state) => state.modals.speedyFlow);
  const [waitingForPayment, setWaitingForPayment] = useState(false);

  useSpeedyFlowAnalytics();

  /**
   * Refetch the user and ask whether the gate that opened this flow would now let them through.
   * That is the only thing that counts as proof of payment (ADR 0003) — not the redirect, and not
   * the tab closing.
   */
  const checkEntitlement = useCallback(async () => {
    if (!gate) {
      return false;
    }
    try {
      const user = await get(buildURL('/users/user'));
      // Written to redux and then read back through the selector, rather than projected a second
      // time from the raw response. The dispatch is synchronous, so the answer is available on the
      // next line — and it is the *same* projection that decided to open this flow in the first
      // place, so the gate cannot be answered two ways for one visitor.
      //
      // Hand-building it here quietly omitted `isSpecialRole`, which `satisfiesGate` checks before
      // it looks at any entitlement. It also had to spell `subscriptionStatus` the API's way rather
      // than the `subscription` redux renames it to — a trap the selector removes entirely.
      dispatch(setUserData(user));
      return satisfiesGate(gate, selectSpeedyFlowUser(store.getState()), Date.now());
    } catch (err) {
      console.error('Could not check entitlement', err);
      return false;
    }
  }, [dispatch, gate, store]);

  const {
    open: openCheckout,
    reserve: reserveCheckoutTab,
    release: releaseCheckoutTab,
    retry,
  } = useCheckoutPopup({
    checkEntitlement,
    onConfirmed: () => {
      setWaitingForPayment(false);
      dispatch(advanceSpeedyFlow({ type: 'paymentConfirmed' }));
    },
    onUnconfirmed: () => {
      setWaitingForPayment(false);
      dispatch(advanceSpeedyFlow({ type: 'paymentUnconfirmed' }));
    },
    onAbandoned: () => {
      setWaitingForPayment(false);
      dispatch(advanceSpeedyFlow({ type: 'paymentAbandoned' }));
    },
  });

  /**
   * Open the Stripe tab for a URL we already have. Used once there is an account to bill.
   */
  const openCheckoutTab = useCallback(
    (url: string, selection: ProductSelection) => {
      // A card that built its own session hands the URL straight here, so the machine has not been
      // told a product was chosen yet. Move it now, or the modal would sit on the product list
      // while Stripe is open in the other tab.
      //
      // The selection has to come with it. This used to record empty ids, which meant a signed-in
      // member's confirmation card could not name what they had just bought and fell back to
      // "your new pass" — the guest path named it, so the two disagreed.
      dispatch(advanceSpeedyFlow({ type: 'choseProduct', ...selection }));
      setWaitingForPayment(true);
      if (!openCheckout(url)) {
        // Popup blocked, and there is no way to keep this page alive. The choice rides back on the
        // return path so the flow can be restored — see `resumeState` for why that is safe.
        window.location.href = url;
      }
    },
    [openCheckout, dispatch],
  );

  /**
   * A signed-out visitor picked a product. Record it and let the machine send them to sign up —
   * a checkout session cannot exist before the account does.
   */
  const onSelectProduct = useCallback(
    (selection: ProductSelection) => {
      dispatch(advanceSpeedyFlow({ type: 'choseProduct', ...selection }));
    },
    [dispatch],
  );

  /**
   * Once the flow reaches the payment step with a choice made earlier but no session yet, create
   * one now. This is the guest path catching up: the product was chosen before the account existed.
   */
  const startedCheckoutRef = useRef(false);
  const productChoice = machine?.productChoice;
  const atPayment = machine?.step === 'await-payment';
  /** A choice made before there was an account to bill; checkout has not begun. */
  const needsStart = !!atPayment && !!productChoice?.productId && !waitingForPayment;

  /**
   * The guard is per visit to the payment step, not per page.
   *
   * This component never unmounts — it renders `null` when there is no flow — so the ref outlives
   * every close and reopen. Cleared only on the way out of a failed attempt, it stayed true after a
   * purchase went through, and the *next* product gate in the same page session opened with a start
   * button that returned immediately and explained nothing. Anything that is not the payment step —
   * the flow closing included — means this attempt is over.
   */
  useEffect(() => {
    if (!atPayment) {
      startedCheckoutRef.current = false;
    }
  }, [atPayment]);

  /**
   * Create the session for a deferred choice and open it.
   *
   * Driven by a click rather than an effect on purpose. The guest path arrives here straight from
   * email verification, and a tab opened outside a user gesture is treated as an unsolicited popup
   * — which would silently demote the visitor to the same-tab fallback and lose the modal.
   */
  /**
   * A checkout session that could not be created.
   *
   * The same answer wherever the request was made from — this component's own deferred start, or a
   * product card's mutation — so both come through here. A card that kept its failure to itself
   * left the tab it had reserved sitting blank and open, and the modal waiting on a payment that
   * had never begun.
   */
  const onCheckoutFailed = useCallback(
    (err: any) => {
      console.error('Could not start checkout', err);
      releaseCheckoutTab();
      setWaitingForPayment(false);
      // A 4xx is the server's answer about this product — they may not have it — so the choice
      // goes with the refusal and the list they land on will not offer it back. Anything else
      // may well work on a second try, so the choice stays. The step normally catches this
      // before the request, from the eligibility the product already carries; this is the race,
      // and the offer check, which only the server can do.
      const refused = err?.statusCode >= 400 && err?.statusCode < 500;
      dispatch(advanceSpeedyFlow({ type: refused ? 'checkoutRefused' : 'paymentAbandoned' }));
    },
    [releaseCheckoutTab, dispatch],
  );

  const startDeferredCheckout = useCallback(() => {
    if (!productChoice?.productId || startedCheckoutRef.current) {
      return;
    }
    startedCheckoutRef.current = true;
    // Claim the tab inside this click, before the async round trip.
    reserveCheckoutTab();
    setWaitingForPayment(true);
    // The marker rides back on `co-redirect`, which is how the return routes know they are a
    // popup. No backend change: successPath is already a parameter, and the server's path
    // validation permits a query string.
    const here = window.location.pathname + window.location.search;
    const returnPath = withResumeState(withPopupMarker(here), productChoice);
    postUserProductMutationFn({ ...productChoice, successPath: returnPath, cancelPath: returnPath })
      .then(({ url }) => openCheckoutTab(url, productChoice))
      .catch((err) => {
        // Let them press it again: this attempt is over, and the step they land on may well offer
        // the same button.
        startedCheckoutRef.current = false;
        onCheckoutFailed(err);
      });
  }, [productChoice, reserveCheckoutTab, openCheckoutTab, onCheckoutFailed]);

  /**
   * Stamp a card-supplied return path with the popup marker, defaulting to the current page when a
   * card did not supply one.
   */
  const markReturnPath = useCallback(
    (path?: string) => withPopupMarker(path ?? window.location.pathname + window.location.search),
    [],
  );

  /**
   * Social login runs in a tab too, for the same reason checkout does: navigating away would throw
   * the modal, and with it any product already chosen. Returns false if no tab could be claimed,
   * which tells the button to navigate instead.
   */
  const { open: openSso } = useSsoPopup({
    onSuccess: () => dispatch(advanceSpeedyFlow({ type: 'ssoSucceeded' })),
    onFailure: () => dispatch(advanceSpeedyFlow({ type: 'ssoFailed' })),
  });

  useResumeState();

  /** How long the history was when the app loaded — the baseline `leaveGatedPage` measures against. */
  const [historyLengthAtLoad] = useState(() => (typeof window === 'undefined' ? 0 : window.history.length));

  /**
   * Do the thing they were originally trying to do.
   *
   * The gate interrupted an intent, so finishing the flow has to finish that intent too — otherwise
   * someone who signed up in order to follow a habitat lands back on the page not following it,
   * with nothing to tell them their click was swallowed.
   *
   * The test is whether the gate is *now* satisfied, not whether the flow "succeeded". That covers
   * the cases a success flag would miss — an upgrade resolved in place, or a visitor who turned out
   * to already hold the entitlement — and correctly skips the ones where they walked away.
   */
  const gateRef = useRef<GateRequirement | undefined>();
  const wasVisible = useRef(false);
  useEffect(() => {
    if (visible) {
      wasVisible.current = true;
      gateRef.current = gate;
      return;
    }
    if (!wasVisible.current) {
      return;
    }
    wasVisible.current = false;
    const closedGate = gateRef.current;
    gateRef.current = undefined;
    const action = takePendingAction();
    if (!action || !closedGate) {
      return;
    }
    // Read the store now rather than subscribing: the user has just changed, and this is the only
    // moment the answer matters.
    if (satisfiesGate(closedGate, selectSpeedyFlowUser(store.getState()), Date.now())) {
      action();
    }
  }, [visible, gate, store]);

  /*
   * Navigating away closes the flow — see `speedyFlowNavigated`, fed by the router in `Main`.
   *
   * This used to be a `popstate` listener here, which only caught the browser's back button.
   * `route()` uses pushState and emits no event, so every programmatic redirect left the modal
   * floating over a page it had nothing to do with.
   */

  const logged = useSelector((state) => state.user.logged);

  /**
   * Keep the post-auth destination pointing back at what they picked.
   *
   * A visitor who leaves to fetch a magic link finishes authenticating in whichever tab their mail
   * client opens, and that tab knows nothing about this one. The link itself cannot carry the
   * choice: the server builds it as `/login?ml=<token>` with no redirect of its own, and where the
   * visitor lands afterwards comes from this cookie. So the choice rides on the cookie, in the same
   * `sf` form the same-tab Stripe fallback already uses — and lands them on the payment step with
   * it pre-filled rather than back at a product list they have already been through.
   *
   * Only while signed out, matching when the destination is recorded at all. Once they are signed
   * in there is no round trip left to survive, and overwriting it would fight the guard that reads
   * it.
   */
  useEffect(() => {
    if (!visible || !productChoice?.productId || logged) {
      return;
    }
    // This page, not the saved destination. That records the last page they were trying to reach
    // and survives a move to /home on purpose, so building on it sent people back to somewhere they
    // had browsed earlier and left — with a product choice attached that belonged to here.
    savePostAuthDestination(withResumeState(`${window.location.pathname}${window.location.search}`, productChoice));
  }, [visible, productChoice, logged]);

  usePersistPersona();

  useReportFacts(visible, gate);

  const landingPath = useDefaultLandingPath();

  /**
   * Take the visitor off a page that is no use to them without the flow.
   *
   * Back rather than a fixed destination: they wanted out, and out is where they came from.
   *
   * Unless there is no "came from". A link opened straight into a fresh tab has nothing behind it,
   * and `history.back()` there does nothing at all — leaving them on exactly the empty page this
   * exists to rescue them from. `history.length` cannot answer that question: the entry a tab opens
   * on counts, so the length is already above one before the visitor has been anywhere. What does
   * answer it is whether the length has *grown* since the app loaded, because that only happens
   * when a navigation pushed an entry. Replacements — stripping `sf`, for one — leave it alone,
   * which is right: they did not move anybody.
   */
  const leaveGatedPage = useCallback(() => {
    if (window.history.length > historyLengthAtLoad) {
      window.history.back();
      return;
    }
    route(landingPath, true);
  }, [landingPath, historyLengthAtLoad]);

  /**
   * Dismissal, and what it means on a page the visitor cannot use.
   *
   * The store is read straight after the dispatch because redux is synchronous and the machine is
   * the authority on whether this was really the end: a dismissal that still owes a persona
   * question keeps the modal open, and leaving mid-question would throw the question away.
   */
  const dismiss = useCallback(() => {
    dispatch(advanceSpeedyFlow({ type: 'dismissed' }));
    if (gate?.exitOnDismiss && !store.getState().modals.speedyFlow.visible) {
      leaveGatedPage();
    }
  }, [dispatch, gate, store, leaveGatedPage]);

  /**
   * The confirmation card's own way out. It has to make the same decision, because a dismissal that
   * detoured through the persona question ends here rather than at `dismiss` — but only when the
   * visitor still cannot use the page. Someone who bought their way in stays: the page works now.
   */
  const finishFromCompletion = useCallback(() => {
    dispatch(closeSpeedyFlow());
    if (gate?.exitOnDismiss && !satisfiesGate(gate, selectSpeedyFlowUser(store.getState()), Date.now())) {
      leaveGatedPage();
    }
  }, [dispatch, gate, store, leaveGatedPage]);

  if (!visible || !machine) {
    return null;
  }

  const { step, completionVariant, unconfirmed } = machine;
  // The completion step is a confirmation rather than a task, so it stays a popup on mobile too.
  const popupOnMobile = step === 'completion';
  // Completion has its own CTA and nothing left to ask, so it is dismissable even on a persistent
  // gate — by then the visitor either has what they came for or has declined it.
  //
  // A checkout open in another tab makes any flow persistent for as long as it is open. The popup
  // reports back here, and a flow that has been dismissed has no machine left to report to: a
  // payment that went through would be swallowed, leaving someone who has just been charged with
  // no confirmation and no completion card. Escape is far too cheap a way to cause that. This is
  // the window between the tab opening and it reporting — before that there is nothing at risk,
  // and after it the machine has already moved on.
  const persistent = (!!gate?.persistent && step !== 'completion') || waitingForPayment;

  const renderStep = () => {
    switch (step) {
      case 'persona':
        return <PersonaStep />;
      case 'sign-up':
        return (
          <AuthPopupProvider value={{ open: openSso, params: { ppt } }}>
            <SignUpStep />
          </AuthPopupProvider>
        );
      case 'log-in':
        return (
          <AuthPopupProvider value={{ open: openSso, params: { ppt } }}>
            <LogInStep />
          </AuthPopupProvider>
        );
      case 'verify-email':
        return <VerifyEmailStep />;
      case 'choose-product':
        return (
          <ChooseProductStep
            interception={
              machine.hasAccount
                ? {
                    onCheckoutUrl: openCheckoutTab,
                    reserveCheckoutTab,
                    markReturnPath,
                    onCheckoutFailed,
                    previousSelection: productChoice,
                  }
                : { onSelectProduct, previousSelection: productChoice }
            }
          />
        );
      case 'await-payment':
        return (
          <AwaitPaymentStep
            unconfirmed={unconfirmed}
            waiting={waitingForPayment}
            needsStart={needsStart}
            onStart={startDeferredCheckout}
            onRetry={retry}
            onChooseAnother={() => dispatch(advanceSpeedyFlow({ type: 'checkoutRefused' }))}
          />
        );
      case 'completion':
        return <CompletionStep variant={completionVariant ?? 'account'} onDone={finishFromCompletion} />;
      default:
        return null;
    }
  };

  return (
    <SpeedyFlowShell onDismiss={dismiss} popupOnMobile={popupOnMobile} stepLabel={stepLabels[step]} persistent={persistent}>
      {renderStep()}
    </SpeedyFlowShell>
  );
};

export default SpeedyFlow;
